// Staff grants (#10718): the normal app's half of opening a client
// environment.
//
// Opening one is a single action, picking the org (slice 1b): the grant is a
// member grant for a fixed twelve hours, with an optional note written from
// inside. The server activates the staff member's collaborator account in
// that org and hands back a one-time code; this tab then goes to the entry
// page, which trades the code for the collaborator's token and turns the tab
// into the client environment (slice 3: same address, same tab). The staff
// member's own account never moves: identity (`fp0052`) reports the open grant
// as `staffGrant`, for the console's open-client row (its timer, Renew and
// End) and the home tab's "Continue in <client>".
//
// Design record: .claude/features/10718-staff-access-architecture/README.md.
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { updateUserData } from "@/redux/userSlice";
import {
  clientEnvironmentEnterUrl,
  holdsTokenFor,
  markTabForClient,
} from "@/services/clientEnvironment";
import { CURRENT_USER_QUERY_KEY } from "./useCurrentUser";

export type GrantScope = "member" | "admin";

/** The open grant as `fp0052` reports it (`staffGrant` in a home tab,
 * `clientEnvironment` in a client tab). */
export interface StaffGrant {
  grantId: number;
  organizationId: string;
  organizationName: string;
  scope: GrantScope;
  /** Optional, written from inside the client environment. */
  note: string | null;
  /** ISO instant; countdowns run to it on the viewer's clock. */
  expiresAt: string;
  /** ISO instant renewing never passes (twenty-four hours from opening). */
  renewableUntil?: string;
  staffName: string;
  staffEmail: string;
}

/** A grant as the grant routes return it. */
export interface StaffGrantOut {
  id: number;
  organization_id: string;
  organization_name: string;
  scope: GrantScope;
  note: string | null;
  expires_at: string;
}

interface OpenedGrant {
  grant: StaffGrantOut;
  exchange_code: string;
}

export const GRANTS_URL = `${BASE_API_URL_V2}/api/v2/staff/grants`;
export const CURRENT_GRANT_URL = `${GRANTS_URL}/current`;
export const REISSUE_CODE_URL = `${CURRENT_GRANT_URL}/exchange-code`;
/** Renew the open grant from home, as the staff member (the console row). */
export const RENEW_GRANT_URL = `${CURRENT_GRANT_URL}/renew`;

/** How long logging out waits for the open grant to end before it goes on
 * without an answer. */
export const GRANT_END_DEADLINE_MS = 5_000;

/** Renew is offered once this little of the working block is left. */
export const RENEW_WINDOW_MINUTES = 30;

export const SCOPE_LABEL: Record<GrantScope, string> = {
  member: "Member access",
  admin: "Admin access",
};

/** Whole minutes until `expiresAt`, never negative. */
export function minutesLeft(expiresAt: string, now: Date = new Date()): number {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  return Math.max(0, Math.floor(ms / 60_000));
}

/** "4h left", "35m left", "1h 20m left", or "expired". */
export function describeMinutesLeft(minutes: number): string {
  if (minutes <= 0) return "expired";
  if (minutes < 60) return `${minutes}m left`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h left` : `${hours}h ${rest}m left`;
}

export type OpenResult = { ok: true } | { ok: false; error: string };

type GrantResponse = Response | { ok: false; status: number };

/** Why the server refused, in its own words, or null when it gave none (a
 * request that never reached it, a body that is not the error shape). */
async function refusalReason(response: GrantResponse): Promise<string | null> {
  if (!("json" in response)) return null;
  try {
    const body = (await response.json()) as { detail?: unknown };
    return typeof body.detail === "string" && body.detail.trim() !== ""
      ? body.detail
      : null;
  } catch {
    return null;
  }
}

/** End the staff member's open grant, as their own account. True when none
 * is left open: it ended, or none was open (404). The grant's token dies
 * with it, in whichever browser holds one. */
export async function endOpenStaffGrant(): Promise<boolean> {
  const response: GrantResponse | undefined = await fpFetch(CURRENT_GRANT_URL, {
    method: "DELETE",
  });
  return response !== undefined && (response.ok || response.status === 404);
}

/** Where this tab goes next: a full load, so the page decides its mode
 * again (`isClientEnvironment`). */
const goTo = (url: string): void => {
  window.location.assign(url);
};

/** This page's address, to come back to inside the client. */
function here(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** The staff console's pages: a client tab does not mount them. */
const CONSOLE_PATH_PREFIXES: readonly string[] = ["/__admin", "/review"];

/** Where a client tab lands for `next`: the client's home in place of a
 * console page, which would be a not-found inside the client. */
export function clientLanding(next: string): string {
  const pathname = next.split(/[?#]/, 1)[0];
  const onConsole = CONSOLE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  return onConsole ? "/" : next;
}

/** The elevation route: admin access on the open grant, with a reason. */
export const ELEVATE_GRANT_URL = `${CURRENT_GRANT_URL}/elevate`;

export function useStaffGrant(go: (url: string) => void = goTo): {
  open: (target: { id: string; name: string }) => Promise<OpenResult>;
  openAsAdmin: (
    target: { id: string; name: string },
    options: { reason: string; next: string }
  ) => Promise<OpenResult>;
  continueInOpenClient: (next?: string) => Promise<OpenResult>;
  end: () => Promise<boolean>;
  renew: () => Promise<boolean>;
  refreshIdentity: () => Promise<void>;
} {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const openGrant = useSelector(
    (state: { userData?: { staffGrant?: StaffGrant | null } }) =>
      state.userData?.staffGrant ?? null
  );

  // Join the open grant in this tab without a new code, when this browser
  // holds that grant's own token. A new code replaces the token every other
  // client tab of this browser is using, and ends their access.
  const markAndGo = useCallback(
    (next: string): OpenResult => {
      markTabForClient();
      go(next);
      return { ok: true };
    },
    [go]
  );

  const refreshIdentity = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY }),
    [queryClient]
  );

  const enter = useCallback(
    async (
      request: () => Promise<GrantResponse>,
      failure: string,
      next: string | null = null
    ): Promise<OpenResult> => {
      const response = await request();
      if (!response?.ok || !("json" in response)) {
        const reason = response ? await refusalReason(response) : null;
        return { ok: false, error: reason ?? failure };
      }
      const opened = (await response.json()) as OpenedGrant;
      // Straight to the entry page: this page is being left, so nothing on
      // it changes first (Hunter, 2026-10-02: the console drew the open
      // client a moment before the tab navigated away).
      go(clientEnvironmentEnterUrl(opened.exchange_code, next));
      return { ok: true };
    },
    [go]
  );

  const open = useCallback(
    async (target: { id: string; name: string }): Promise<OpenResult> => {
      if (
        openGrant?.organizationId === target.id &&
        holdsTokenFor(openGrant.grantId)
      ) {
        return markAndGo("/");
      }
      // A staff member works in their own organization as themselves; the
      // server refuses a grant into it, and its reason is what is shown.
      return enter(
        () =>
          fpFetch(GRANTS_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ organization_id: target.id }),
          }),
        `Could not open ${target.name}.`
      );
    },
    [enter, markAndGo, openGrant]
  );

  // Open the organization and take admin access in it, from this (the
  // staff member's own) session, then land on `next` inside it: the console's
  // way into an organization's configuration and into the template
  // organization (#10718, stream F2). The grant and the elevation are the
  // same records and feed lines as the client tab's "Admin access".
  const openAsAdmin = useCallback(
    async (
      target: { id: string; name: string },
      options: { reason: string; next: string }
    ): Promise<OpenResult> => {
      const failure = `Could not open ${target.name} as its admin.`;
      const holdsIt =
        openGrant?.organizationId === target.id &&
        holdsTokenFor(openGrant.grantId);
      let code: string | null = null;
      if (!holdsIt) {
        const opened: GrantResponse | undefined = await fpFetch(GRANTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ organization_id: target.id }),
        });
        if (!opened?.ok || !("json" in opened)) {
          const reason = opened ? await refusalReason(opened) : null;
          return { ok: false, error: reason ?? failure };
        }
        code = ((await opened.json()) as OpenedGrant).exchange_code;
      }
      const elevated: GrantResponse | undefined = await fpFetch(
        ELEVATE_GRANT_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reason: options.reason.trim(),
            organization_id: target.id,
          }),
        }
      );
      if (!elevated?.ok) {
        const reason = elevated ? await refusalReason(elevated) : null;
        return { ok: false, error: reason ?? failure };
      }
      if (code === null) return markAndGo(options.next);
      go(clientEnvironmentEnterUrl(code, options.next));
      return { ok: true };
    },
    [go, markAndGo, openGrant]
  );

  const reissue = useCallback(
    (next: string | null): Promise<OpenResult> =>
      enter(
        () => fpFetch(REISSUE_CODE_URL, { method: "POST" }),
        "Could not open the client environment.",
        next
      ),
    [enter]
  );

  // Into the open grant from a home tab, at `next` (this page by default):
  // the account menu's "Continue in <client>". When this
  // browser holds that grant's own token the tab only needs marking; any
  // other token here (an ended grant's, someone else's) is never reused, and
  // a fresh code is drawn instead.
  const continueInOpenClient = useCallback(
    async (next: string = here()): Promise<OpenResult> => {
      const landing = clientLanding(next);
      return holdsTokenFor(openGrant?.grantId)
        ? markAndGo(landing)
        : reissue(landing);
    },
    [markAndGo, openGrant, reissue]
  );

  const end = useCallback(async (): Promise<boolean> => {
    // Nothing open (404) is the same end state as having ended it.
    if (!(await endOpenStaffGrant())) return false;
    dispatch(updateUserData({ staffGrant: null }));
    await refreshIdentity();
    return true;
  }, [dispatch, refreshIdentity]);

  // The same window and cap as the client tab's own renew; the new expiry
  // arrives with identity.
  const renew = useCallback(async (): Promise<boolean> => {
    const response: GrantResponse | undefined = await fpFetch(RENEW_GRANT_URL, {
      method: "POST",
    });
    if (!response?.ok) return false;
    await refreshIdentity();
    return true;
  }, [refreshIdentity]);

  return {
    open,
    openAsAdmin,
    continueInOpenClient,
    end,
    renew,
    refreshIdentity,
  };
}

/** While a grant is open, re-read identity when this tab comes back: ending
 * access in the client's own tab ends the grant there, and the console must
 * not keep showing it (#10718 review L3). */
export function useRereadIdentityOnReturn(active: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!active) return undefined;
    const reread = (): void => {
      if (document.visibilityState === "visible") {
        void queryClient.invalidateQueries({
          queryKey: CURRENT_USER_QUERY_KEY,
        });
      }
    };
    window.addEventListener("focus", reread);
    document.addEventListener("visibilitychange", reread);
    return () => {
      window.removeEventListener("focus", reread);
      document.removeEventListener("visibilitychange", reread);
    };
  }, [active, queryClient]);
}
